import { AppLayout } from "@/components/layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useGetMySessions } from "@workspace/api-client-react";
import { apiFetch } from "@/lib/api";
import {
  format, startOfWeek, endOfWeek, addWeeks, subWeeks,
  eachDayOfInterval, isSameDay, parseISO, differenceInMinutes,
  isToday, addDays,
} from "date-fns";
import { Calendar, Clock, Check, X, Video, List, ChevronLeft, ChevronRight, ClipboardList, Users, MapPin, CreditCard, Crown, Package, AlertTriangle } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { SessionNotePanel } from "@/components/session-note-panel";
import { PayoutBreakdown } from "@/components/payout-breakdown";

const HOUR_START = 6;   // 6 AM
const HOUR_END = 22;    // 10 PM
const TOTAL_HOURS = HOUR_END - HOUR_START;
const HOUR_HEIGHT = 64; // px per hour
const CALENDAR_HEIGHT = TOTAL_HOURS * HOUR_HEIGHT;

const STATUS_STYLES: Record<string, string> = {
  CONFIRMED:  "bg-blue-500/20 border-blue-500/40 text-blue-300",
  PENDING:    "bg-amber-500/20 border-amber-500/40 text-amber-300",
  COMPLETED:  "bg-green-500/20 border-green-500/40 text-green-300",
  CANCELLED:  "bg-red-500/20 border-red-500/40 text-red-300",
  REJECTED:   "bg-red-500/20 border-red-500/40 text-red-300",
  NO_SHOW:    "bg-orange-500/20 border-orange-500/40 text-orange-300",
};

const STATUS_DOT: Record<string, string> = {
  CONFIRMED: "bg-blue-400",
  PENDING:   "bg-amber-400",
  COMPLETED: "bg-green-400",
  CANCELLED: "bg-red-400",
  REJECTED:  "bg-red-400",
  NO_SHOW:   "bg-orange-400",
};

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function TrainerSessions() {
  const { user } = useAuth();
  const { data: sessions, isLoading, refetch } = useGetMySessions();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [tab, setTab] = useState<"pending" | "upcoming" | "completed" | "no-shows" | "all">("upcoming");
  const [view, setView] = useState<"calendar" | "list" | "waitlist">("calendar");
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedSession, setSelectedSession] = useState<any | null>(null);

  const { data: trainerWaitlist = [] } = useQuery<any[]>({
    queryKey: ["trainer-my-waitlist"],
    queryFn: () => apiFetch("/trainers/my/waitlist"),
    enabled: !!user,
  });

  const pending  = sessions?.filter((s: any) => s.status === "PENDING") || [];
  const upcoming = sessions?.filter((s: any) => s.status === "CONFIRMED" && new Date(s.proposedStart) > new Date()) || [];
  const completed = sessions?.filter((s: any) => s.status === "COMPLETED") || [];
  const noShows = sessions?.filter((s: any) => s.status === "NO_SHOW") || [];

  const weekDays = useMemo(
    () => eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) }),
    [weekStart]
  );

  const weekSessions = useMemo(
    () => (sessions || []).filter((s: any) => {
      const d = parseISO(s.proposedStart);
      return d >= weekStart && d <= endOfWeek(weekStart, { weekStartsOn: 1 });
    }),
    [sessions, weekStart]
  );

  async function handleAction(id: string, action: "confirm" | "reject" | "complete" | "flag-no-show") {
    setActionLoading(id);
    try {
      await apiFetch(`/sessions/${id}/${action}`, { method: "PUT", body: JSON.stringify({}) });
      const labels: Record<string, string> = { confirm: "confirmed", reject: "declined", complete: "completed", "flag-no-show": "flagged as no-show" };
      toast({ title: `Session ${labels[action]}` });
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  }

  const listTabs = [
    { key: "upcoming"  as const, label: "Upcoming",  count: upcoming.length },
    { key: "pending"   as const, label: "Requests",  count: pending.length  },
    { key: "completed" as const, label: "Completed", count: completed.length },
    { key: "no-shows"  as const, label: "No-Shows",  count: noShows.length  },
    { key: "all"       as const, label: "All",       count: sessions?.length || 0 },
  ];
  const filtered = tab === "pending" ? pending : tab === "upcoming" ? upcoming : tab === "completed" ? completed : tab === "no-shows" ? noShows : sessions || [];

  return (
    <AppLayout>
      <div className="flex flex-col gap-6 pb-12">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-1">My Sessions</h1>
            <p className="text-muted-foreground">Manage and view your training schedule</p>
          </div>
          <div className="flex items-center gap-1 p-1 rounded-lg bg-white/5 border border-white/10 shrink-0">
            <button
              onClick={() => setView("calendar")}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all",
                view === "calendar" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-white"
              )}
            >
              <Calendar className="w-4 h-4" /> Calendar
            </button>
            <button
              onClick={() => setView("list")}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all",
                view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-white"
              )}
            >
              <List className="w-4 h-4" /> List
            </button>
            <button
              onClick={() => setView("waitlist")}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all relative",
                view === "waitlist" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-white"
              )}
            >
              <ClipboardList className="w-4 h-4" /> Waitlist
              {trainerWaitlist.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-400 text-black text-[10px] font-bold flex items-center justify-center">
                  {trainerWaitlist.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {view === "waitlist" ? (
          <TrainerWaitlistPanel waitlist={trainerWaitlist} />
        ) : view === "calendar" ? (
          <WeekCalendar
            weekDays={weekDays}
            weekSessions={weekSessions}
            isLoading={isLoading}
            onPrev={() => setWeekStart(w => subWeeks(w, 1))}
            onNext={() => setWeekStart(w => addWeeks(w, 1))}
            onToday={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
            onAction={handleAction}
            actionLoading={actionLoading}
            onJoinVideo={(id) => navigate(`/video/${id}`)}
            onSelectSession={setSelectedSession}
          />
        ) : (
          <ListView
            tabs={listTabs}
            tab={tab}
            setTab={setTab}
            filtered={filtered}
            isLoading={isLoading}
            actionLoading={actionLoading}
            onAction={handleAction}
            onJoinVideo={(id) => navigate(`/video/${id}`)}
            onSelectSession={setSelectedSession}
          />
        )}
      </div>

      <SessionDetailSheet
        session={selectedSession}
        open={!!selectedSession}
        onClose={() => setSelectedSession(null)}
        onAction={(id, action) => { handleAction(id, action); setSelectedSession(null); }}
        actionLoading={actionLoading}
        onJoinVideo={(id) => { navigate(`/video/${id}`); setSelectedSession(null); }}
      />
    </AppLayout>
  );
}

/* ─── Session Detail Sheet ───────────────────────────────────────────────── */
const SESSION_STATUS_STYLES: Record<string, string> = {
  COMPLETED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  CONFIRMED: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  PENDING: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  CANCELLED: "bg-white/5 text-muted-foreground border-white/10",
  REJECTED: "bg-red-500/10 text-red-400 border-red-500/20",
  NO_SHOW: "bg-orange-500/10 text-orange-400 border-orange-500/30",
};

function SessionDetailSheet({
  session,
  open,
  onClose,
  onAction,
  actionLoading,
  onJoinVideo,
}: {
  session: any | null;
  open: boolean;
  onClose: () => void;
  onAction: (id: string, action: "confirm" | "reject" | "complete" | "flag-no-show") => void;
  actionLoading: string | null;
  onJoinVideo: (id: string) => void;
}) {
  const [noShowConfirm, setNoShowConfirm] = useState(false);

  if (!session) return null;
  const start = parseISO(session.proposedStart);
  const end = parseISO(session.proposedEnd);
  const isPast = new Date(session.proposedEnd) < new Date();
  const canWriteNote = session.status === "COMPLETED" || session.status === "CONFIRMED";

  return (
    <Sheet open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto bg-background border-white/10">
        <SheetHeader className="pb-4 border-b border-white/10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-lg font-bold text-primary shrink-0">
              {session.student?.firstName?.[0] ?? "?"}{session.student?.lastName?.[0] ?? ""}
            </div>
            <div className="min-w-0">
              <SheetTitle className="text-white text-lg">
                {session.student?.firstName} {session.student?.lastName}
              </SheetTitle>
              <Badge
                variant="outline"
                className={`mt-1 text-xs ${SESSION_STATUS_STYLES[session.status] ?? SESSION_STATUS_STYLES.CANCELLED}`}
              >
                {session.status.charAt(0) + session.status.slice(1).toLowerCase()}
              </Badge>
              {(session as any).lateCancellation && (
                <Badge variant="outline" className="mt-1 text-xs border-orange-500/40 bg-orange-500/10 text-orange-400">
                  Late Cancel — Fee Charged
                </Badge>
              )}
            </div>
          </div>
        </SheetHeader>

        <div className="pt-4 space-y-4">
          {/* Date / time / mode */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-white">
              <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
              {format(start, "EEEE, MMMM d, yyyy")}
            </div>
            <div className="flex items-center gap-2 text-white">
              <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
              {format(start, "h:mm a")} – {format(end, "h:mm a")}
              <span className="text-muted-foreground text-xs">({session.durationMinutes ?? differenceInMinutes(end, start)} min)</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              {session.mode === "VIRTUAL" || session.mode === "VIDEO"
                ? <Video className="w-4 h-4 text-muted-foreground shrink-0" />
                : <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />}
              {session.mode === "VIRTUAL" || session.mode === "VIDEO" ? "Virtual / Video" : "In-person"}
            </div>
            {session.trainerPayout != null && (
              <div className="flex items-center gap-2 text-white">
                <CreditCard className="w-4 h-4 text-muted-foreground shrink-0" />
                <PayoutBreakdown session={session} />
                <span className="text-muted-foreground text-xs">your payout</span>
              </div>
            )}
            {session.membershipId && (
              <div className="flex items-center gap-2 text-xs text-primary">
                <Crown className="w-3.5 h-3.5" /> Paid via membership credit
              </div>
            )}
            {session.paymentType === "PACKAGE" && (
              <div className="flex items-center gap-2 text-xs text-purple-400">
                <Package className="w-3.5 h-3.5" /> Paid via package
              </div>
            )}
          </div>

          {/* Action buttons */}
          {session.status === "PENDING" && (
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                disabled={actionLoading === session.id}
                onClick={() => { onAction(session.id, "confirm"); onClose(); }}
                className="flex-1 bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
              >
                <Check className="w-3.5 h-3.5 mr-1.5" /> Accept
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={actionLoading === session.id}
                onClick={() => { onAction(session.id, "reject"); onClose(); }}
                className="flex-1 text-red-400 hover:bg-red-500/10"
              >
                <X className="w-3.5 h-3.5 mr-1.5" /> Decline
              </Button>
            </div>
          )}
          {session.status === "CONFIRMED" && isPast && !noShowConfirm && (
            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1"
                disabled={actionLoading === session.id}
                onClick={() => { onAction(session.id, "complete"); onClose(); }}
              >
                <Check className="w-3.5 h-3.5 mr-1.5" /> Mark Complete
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="flex-1 text-orange-400 border border-orange-500/30 hover:bg-orange-500/10"
                disabled={actionLoading === session.id}
                onClick={() => setNoShowConfirm(true)}
              >
                <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> No-Show
              </Button>
            </div>
          )}
          {session.status === "CONFIRMED" && isPast && noShowConfirm && (
            <div className="p-3 rounded-xl bg-orange-500/10 border border-orange-500/30 space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-orange-300">Flag as no-show?</p>
                  <p className="text-xs text-orange-400/80 mt-0.5">
                    This will charge {session.student?.firstName}'s session credit and initiate your payout. The student will be notified.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="flex-1 bg-orange-500 text-white hover:bg-orange-600"
                  disabled={actionLoading === session.id}
                  onClick={() => { onAction(session.id, "flag-no-show"); setNoShowConfirm(false); onClose(); }}
                >
                  {actionLoading === session.id ? "Flagging…" : "Confirm No-Show"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground"
                  onClick={() => setNoShowConfirm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {session.status === "NO_SHOW" && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-orange-500/10 border border-orange-500/30">
              <AlertTriangle className="w-4 h-4 text-orange-400 shrink-0" />
              <p className="text-xs text-orange-300">
                Flagged as no-show. Credit charged and payout initiated.
              </p>
            </div>
          )}
          {(session.mode === "VIDEO" || session.mode === "VIRTUAL") && session.status === "CONFIRMED" && (
            <Button
              size="sm"
              className="w-full bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => { onJoinVideo(session.id); onClose(); }}
            >
              <Video className="w-3.5 h-3.5 mr-1.5" /> Join Video Call
            </Button>
          )}

          {/* Session notes */}
          {canWriteNote && (
            <div className="pt-2">
              <SessionNotePanel
                sessionId={session.id}
                otherPartyLabel={session.student?.firstName ?? "student"}
              />
            </div>
          )}
          {!canWriteNote && session.status === "PENDING" && (
            <p className="text-xs text-muted-foreground pt-2">
              Session notes become available once this session is confirmed.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ─── Trainer Waitlist Panel ─────────────────────────────────────────────── */
function TrainerWaitlistPanel({ waitlist }: { waitlist: any[] }) {
  if (waitlist.length === 0) {
    return (
      <div className="p-12 text-center rounded-2xl border border-dashed border-white/10 bg-white/5">
        <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
        <p className="text-white text-lg font-medium">No students on your waitlist</p>
        <p className="text-muted-foreground text-sm mt-2">Students who want to train with you but can't find an open slot will appear here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <Users className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold text-white">Students Waiting</h2>
        <span className="text-sm text-muted-foreground">({waitlist.length} {waitlist.length === 1 ? "student" : "students"})</span>
      </div>
      <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 text-sm text-amber-300">
        When a confirmed session is cancelled, the first student in line is automatically notified.
      </div>
      {waitlist.map((entry: any, idx: number) => (
        <div key={entry.id} className="glass-panel border border-white/5 rounded-2xl p-5 flex items-center gap-4">
          <span className="text-muted-foreground font-mono text-xs w-5 shrink-0">#{idx + 1}</span>
          <div className="w-11 h-11 rounded-full bg-secondary border border-white/10 flex items-center justify-center text-base font-bold text-white shrink-0">
            {entry.student?.firstName?.[0] ?? "S"}{entry.student?.lastName?.[0] ?? ""}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold">{entry.student?.firstName} {entry.student?.lastName}</p>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground flex-wrap">
              {entry.dayOfWeek !== null && entry.dayOfWeek !== undefined && (
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {DAY_NAMES[entry.dayOfWeek]}s</span>
              )}
              {entry.timeSlot && (
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {entry.timeSlot}</span>
              )}
              <span>Joined {format(new Date(entry.createdAt), "MMM d, yyyy")}</span>
            </div>
          </div>
          {entry.notifiedAt && (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-full shrink-0">
              Notified
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─── Week Calendar ─────────────────────────────────────────────────────── */
function WeekCalendar({
  weekDays, weekSessions, isLoading, onPrev, onNext, onToday, onAction, actionLoading, onJoinVideo, onSelectSession,
}: {
  weekDays: Date[];
  weekSessions: any[];
  isLoading: boolean;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onAction: (id: string, action: "confirm" | "reject" | "complete") => void;
  actionLoading: string | null;
  onJoinVideo: (id: string) => void;
  onSelectSession: (session: any) => void;
}) {
  const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => HOUR_START + i);
  const now = new Date();
  const nowMinutes = (now.getHours() - HOUR_START) * 60 + now.getMinutes();
  const nowTop = (nowMinutes / 60) * HOUR_HEIGHT;
  const showNowLine = nowMinutes >= 0 && nowMinutes <= TOTAL_HOURS * 60;

  return (
    <div className="flex flex-col gap-4">
      {/* Week nav */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onPrev} className="border-white/10 hover:bg-white/5">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onNext} className="border-white/10 hover:bg-white/5">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onToday} className="border-white/10 hover:bg-white/5 text-sm">
            Today
          </Button>
        </div>
        <span className="text-white font-semibold text-sm">
          {format(weekDays[0], "MMM d")} – {format(weekDays[6], "MMM d, yyyy")}
        </span>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400 inline-block" /> Confirmed</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> Pending</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" /> Done</span>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="rounded-xl border border-white/10 overflow-hidden bg-card">
        {/* Day headers */}
        <div className="grid border-b border-white/10" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
          <div className="border-r border-white/10" />
          {weekDays.map((day) => (
            <div
              key={day.toISOString()}
              className={cn(
                "py-3 text-center border-r border-white/10 last:border-r-0",
                isToday(day) && "bg-primary/5"
              )}
            >
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{format(day, "EEE")}</p>
              <p className={cn(
                "text-lg font-bold mt-0.5",
                isToday(day) ? "text-primary" : "text-white"
              )}>
                {format(day, "d")}
              </p>
            </div>
          ))}
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto" style={{ maxHeight: "600px" }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-24 text-muted-foreground text-sm">Loading...</div>
          ) : (
            <div className="relative grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)", height: `${CALENDAR_HEIGHT}px` }}>
              {/* Hour labels */}
              <div className="relative border-r border-white/10">
                {hours.map((h) => (
                  <div
                    key={h}
                    className="absolute w-full flex items-start justify-end pr-2"
                    style={{ top: `${(h - HOUR_START) * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
                  >
                    <span className="text-[10px] text-muted-foreground/60 leading-none pt-1">
                      {h === 12 ? "12p" : h > 12 ? `${h - 12}p` : `${h}a`}
                    </span>
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {weekDays.map((day, colIdx) => {
                const daySessions = weekSessions.filter((s: any) => isSameDay(parseISO(s.proposedStart), day));
                return (
                  <div
                    key={day.toISOString()}
                    className={cn(
                      "relative border-r border-white/10 last:border-r-0",
                      isToday(day) && "bg-primary/[0.02]"
                    )}
                  >
                    {/* Hour lines */}
                    {hours.map((h) => (
                      <div
                        key={h}
                        className="absolute w-full border-t border-white/5"
                        style={{ top: `${(h - HOUR_START) * HOUR_HEIGHT}px` }}
                      />
                    ))}

                    {/* Now line */}
                    {isToday(day) && showNowLine && (
                      <div
                        className="absolute left-0 right-0 z-20 flex items-center"
                        style={{ top: `${nowTop}px` }}
                      >
                        <div className="w-2 h-2 rounded-full bg-primary -ml-1 shrink-0" />
                        <div className="flex-1 border-t border-primary" />
                      </div>
                    )}

                    {/* Sessions */}
                    {daySessions.map((session: any) => {
                      const start = parseISO(session.proposedStart);
                      const end = parseISO(session.proposedEnd);
                      const startMin = (start.getHours() - HOUR_START) * 60 + start.getMinutes();
                      const duration = differenceInMinutes(end, start);
                      const top = (startMin / 60) * HOUR_HEIGHT;
                      const height = Math.max((duration / 60) * HOUR_HEIGHT, 28);
                      const style = STATUS_STYLES[session.status] || "bg-white/10 border-white/20 text-muted-foreground";

                      return (
                        <div
                          key={session.id}
                          onClick={() => onSelectSession(session)}
                          className={cn(
                            "absolute left-0.5 right-0.5 rounded-md border px-1.5 py-1 overflow-hidden z-10 cursor-pointer group hover:brightness-125 transition-[filter]",
                            style
                          )}
                          style={{ top: `${top}px`, height: `${height}px` }}
                          title={`${session.student?.firstName} ${session.student?.lastName} — ${format(start, "h:mm a")} to ${format(end, "h:mm a")} · click for details`}
                        >
                          <p className="text-[11px] font-semibold leading-tight truncate">
                            {session.student?.firstName} {session.student?.lastName?.[0]}.
                          </p>
                          {height > 40 && (
                            <p className="text-[10px] opacity-70 leading-tight">
                              {format(start, "h:mm")}–{format(end, "h:mm a")}
                            </p>
                          )}
                          {height > 60 && session.status === "PENDING" && (
                            <div className="flex gap-1 mt-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); onAction(session.id, "confirm"); }}
                                disabled={actionLoading === session.id}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/30 text-green-300 hover:bg-green-500/50 leading-none"
                              >✓</button>
                              <button
                                onClick={(e) => { e.stopPropagation(); onAction(session.id, "reject"); }}
                                disabled={actionLoading === session.id}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/30 text-red-300 hover:bg-red-500/50 leading-none"
                              >✕</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Pending requests callout */}
      {weekSessions.some((s: any) => s.status === "PENDING") && (
        <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/5">
          <p className="text-sm font-medium text-amber-300 mb-3">
            Pending requests this week — accept or decline:
          </p>
          <div className="space-y-2">
            {weekSessions.filter((s: any) => s.status === "PENDING").map((session: any) => (
              <div key={session.id} className="flex items-center justify-between gap-4 p-3 rounded-lg bg-amber-500/5 border border-amber-500/10">
                <div>
                  <p className="text-sm font-semibold text-white">
                    {session.student?.firstName} {session.student?.lastName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(parseISO(session.proposedStart), "EEE MMM d · h:mm a")} – {format(parseISO(session.proposedEnd), "h:mm a")}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" disabled={actionLoading === session.id} onClick={() => onAction(session.id, "confirm")}
                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30">
                    <Check className="w-3.5 h-3.5 mr-1" /> Accept
                  </Button>
                  <Button size="sm" variant="ghost" disabled={actionLoading === session.id} onClick={() => onAction(session.id, "reject")}
                    className="text-red-400 hover:bg-red-500/10">
                    <X className="w-3.5 h-3.5 mr-1" /> Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── List View ─────────────────────────────────────────────────────────── */
function ListView({
  tabs, tab, setTab, filtered, isLoading, actionLoading, onAction, onJoinVideo, onSelectSession,
}: {
  tabs: { key: "pending" | "upcoming" | "completed" | "no-shows" | "all"; label: string; count: number }[];
  tab: "pending" | "upcoming" | "completed" | "no-shows" | "all";
  setTab: (t: "pending" | "upcoming" | "completed" | "no-shows" | "all") => void;
  filtered: any[];
  isLoading: boolean;
  actionLoading: string | null;
  onAction: (id: string, action: "confirm" | "reject" | "complete" | "flag-no-show") => void;
  onJoinVideo: (id: string) => void;
  onSelectSession: (session: any) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2 border-b border-white/10 pb-4 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              "px-4 py-2 rounded-lg text-sm font-medium transition-all",
              tab === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-white/5"
            )}
          >
            {t.label} <span className="ml-1 font-mono opacity-70">({t.count})</span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center rounded-2xl border border-dashed border-white/10 bg-white/5">
          <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-white font-medium text-lg">No {tab} sessions</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((session: any) => (
            <Card key={session.id} className="glass-panel border-white/5">
              <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-full bg-primary/20 flex items-center justify-center text-lg font-bold text-primary shrink-0">
                      {session.student?.firstName?.[0] || "?"}
                    </div>
                    <div>
                      <h4 className="font-bold text-white">
                        {session.student?.firstName} {session.student?.lastName}
                      </h4>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {format(parseISO(session.proposedStart), "EEE, MMM d, yyyy")}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {format(parseISO(session.proposedStart), "h:mm a")} – {format(parseISO(session.proposedEnd), "h:mm a")}
                        </span>
                        {session.mode === "VIDEO" && (
                          <span className="flex items-center gap-1 text-blue-400"><Video className="w-3.5 h-3.5" /> Video</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <PayoutBreakdown session={session} />
                        <span className="text-xs text-muted-foreground">payout</span>
                        <span className={cn(
                          "text-xs px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider flex items-center gap-1",
                          session.status === "COMPLETED" ? "bg-green-500/10 text-green-400" :
                          session.status === "CONFIRMED" ? "bg-blue-500/10 text-blue-400" :
                          session.status === "PENDING"   ? "bg-amber-500/10 text-amber-400" :
                          "bg-red-500/10 text-red-400"
                        )}>
                          <span className={cn("w-1.5 h-1.5 rounded-full", STATUS_DOT[session.status] || "bg-muted")} />
                          {session.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {session.mode === "VIDEO" && session.status === "CONFIRMED" && (
                      <Button size="sm" onClick={() => onJoinVideo(session.id)} className="bg-blue-600 text-white hover:bg-blue-700">
                        <Video className="w-4 h-4 mr-1" /> Join Video
                      </Button>
                    )}
                    {session.status === "PENDING" && (
                      <>
                        <Button size="sm" disabled={actionLoading === session.id} onClick={() => onAction(session.id, "confirm")}
                          className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30">
                          <Check className="w-4 h-4 mr-1" /> Accept
                        </Button>
                        <Button size="sm" variant="ghost" disabled={actionLoading === session.id} onClick={() => onAction(session.id, "reject")}
                          className="text-red-400 hover:bg-red-500/10">
                          <X className="w-4 h-4 mr-1" /> Decline
                        </Button>
                      </>
                    )}
                    {session.status === "CONFIRMED" && new Date(session.proposedEnd) < new Date() && (
                      <Button size="sm" disabled={actionLoading === session.id} onClick={() => onAction(session.id, "complete")}
                        className="bg-primary text-primary-foreground hover:bg-primary/80">
                        <Check className="w-4 h-4 mr-1" /> Complete
                      </Button>
                    )}
                  </div>
                </div>
                {(session.status === "COMPLETED" || session.status === "CONFIRMED") && (
                  <SessionNotePanel
                    sessionId={session.id}
                    otherPartyLabel={`${session.student?.firstName || "student"}`}
                  />
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
